import { ReactNode, useMemo } from 'react';

import {
  ChartContainer,
  ChartGrid,
  ChartTooltip,
  ChartTooltipTimestampHeader,
  Line,
  TimeXAxis,
  YAxis,
  Box,
  type ChartContainerProps,
} from '@cast/design-system';
import type { TimeUnit } from '@cast/shared-types';
import { dashesOrNum, isE2E } from '@cast/utils';
import { attachTimeTicks, fillGaps } from '@cast/split-me/utils';
import { HiddenDot } from '@cast/split-me/components';
import { TimeSeriesEntryWithTicks } from '@cast/split-me/types';

type ResourceChartDataSetting = {
  color: string;
  title: string;
  tooltipTitle: string;
  dashed?: boolean;
  tooltipText?: string;
};

type DateTime = string | number | Date;

export type ResourceChartArgs<T extends { timestamp: string }> = {
  data: T[];
  isLoading: boolean;
  isMetricDisabled: (key: string) => boolean;
  unit: string;
  timeZone: string;
  chartDataSettings: Record<string, ResourceChartDataSetting>;
  testId?: string;
  children?: ReactNode;
  dateRange?: { from: DateTime; to: DateTime };
  timeUnit?: TimeUnit;
  step?: number;
  filler?: (timestamp: DateTime) => Partial<T>;
  yAxisFormatter?: (value: number) => string;
  zoomProps?: ChartContainerProps<unknown>['zoomProps'];
  hasNoData?: boolean;
  noDataOverlayProps?: ChartContainerProps<unknown>['noDataOverlayProps'];
};

export const ResourceChart = <T extends { timestamp: string }>({
  data,
  isLoading,
  isMetricDisabled,
  unit,
  timeZone,
  chartDataSettings,
  testId = 'resource-chart',
  children,
  dateRange,
  timeUnit,
  step,
  filler,
  yAxisFormatter,
  zoomProps,
  hasNoData,
  noDataOverlayProps,
}: ResourceChartArgs<T>) => {
  const chartData = useMemo(() => {
    const filled =
      dateRange && timeUnit && filler
        ? (fillGaps({
            timeSeries: data,
            dateRange,
            filler,
            unit: timeUnit,
            step,
          }) as T[])
        : data;
    return attachTimeTicks(filled);
  }, [data, dateRange, timeUnit, step, filler]);

  return (
    <Box flexGrow={1} width={0}>
      <ChartContainer
        data={chartData}
        isLoading={isLoading}
        hasNoData={hasNoData}
        noDataOverlayProps={noDataOverlayProps}
        chartActionsSx={{ top: 0, right: 0 }}
        testId={testId}
        zoomProps={zoomProps}
      >
        <ChartGrid hideVertical />

        {children}

        {Object.keys(chartDataSettings).map(
          (dataKey) =>
            !isMetricDisabled(dataKey) && (
              <Line<T>
                key={dataKey}
                dataKey={dataKey}
                stroke={chartDataSettings[dataKey].color}
                strokeWidth={1.5}
                strokeDasharray={
                  chartDataSettings[dataKey].dashed ? '4 4' : undefined
                }
                isAnimationActive={false}
                tooltipConfig={{
                  displayName: chartDataSettings[dataKey].tooltipTitle,
                  unit,
                }}
                dot={isE2E() ? HiddenDot : false}
              />
            )
        )}

        <TimeXAxis unixDataKey="chartTimestampUnix" timezone={timeZone} />
        <YAxis
          tickFormatter={yAxisFormatter}
          unitFormatter={yAxisFormatter ? undefined : (v) => `${v} ${unit}`}
        />

        <ChartTooltip<TimeSeriesEntryWithTicks<object>>
          testId="data-popper"
          header={(headerProps) => (
            <ChartTooltipTimestampHeader {...headerProps} timezone={timeZone} />
          )}
          formatValue={(value) => dashesOrNum({ value, roundBy: 2 })}
        />
      </ChartContainer>
    </Box>
  );
};
